import { gql, QueryResult, useMutation, useQuery } from '@apollo/client';
import {
  InviteByToken,
  InviteByTokenVariables,
  InviteByToken_inviteByToken_invite,
} from 'types/generated/InviteByToken';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { useEffect, useState, useMemo } from 'react';
import { useDeviceId } from 'utils/use-device-id';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { InviteStatus } from 'types/generated/globalTypes';
import {
  AnswerInvite,
  AnswerInviteVariables,
} from 'types/generated/AnswerInvite';
import { useInviteToken } from './onboarding/common/hooks';
import { InviteQuery } from './onboarding/common/queries';
import { OnboardingFlow } from './onboarding/onboarding-flow/OnboardingFlow';
import { useServerProps } from '../../../utils/ServerProps';
import { SpinnerFullPage } from '@updater/ui-uds';

const FormWrapper = styled.div`
  padding-bottom: 150px;
`;

const answerInvite = gql`
  mutation AnswerInvite($input: AnswerInviteInput!) {
    answerInvite(input: $input) {
      invite {
        id
      }
    }
  }
`;

const useHandleInviteStatus = ({
  data,
}: QueryResult<InviteByToken, InviteByTokenVariables>) => {
  const router = useRouter();
  const inviteToken = useInviteToken();
  const { env } = useServerProps();
  const [handled, setHandled] = useState(false);
  const [answerInviteFn] = useMutation<AnswerInvite, AnswerInviteVariables>(
    answerInvite,
    {
      variables: {
        input: {
          token: inviteToken || '',
        },
      },
    }
  );
  useEffect(() => {
    if (data && !handled) {
      setHandled(true);
      if (data.inviteByToken?.invite?.status === InviteStatus.CLAIMED) {
        router.push('/');
      } else if (
        !!inviteToken &&
        data.inviteByToken?.invite &&
        data.inviteByToken.invite.status !== InviteStatus.ANSWERED
      ) {
        answerInviteFn();
      }
    }
  }, [data, answerInviteFn, router, handled, setHandled, env, inviteToken]);
};

const useInvite = () => {
  const inviteToken = useInviteToken();
  const result = useQuery<InviteByToken, InviteByTokenVariables>(InviteQuery, {
    variables: {
      token: inviteToken || '',
    },
    skip: !inviteToken,
  });

  useHandleInviteStatus(result);
  return result;
};

const OnboardingInner: React.FC<{
  invite?: InviteByToken_inviteByToken_invite;
}> = ({ invite }) => {
  const { Track } = useTracking<TrackEventInput<unknown>>({
    details: {
      invite: {
        flow: 'default',
        uuid: invite?.uuid,
      },
    },
  });

  return (
    <Track>
      <FormWrapper>
        <OnboardingFlow invite={invite} />
      </FormWrapper>
    </Track>
  );
};

let cachedInvite: InviteByToken | null = null;

export default function OnboardingWrapper() {
  const router = useRouter();
  const inviteToken = useInviteToken();
  const deviceId = useDeviceId();
  const { data, error } = useInvite();
  const inviteData = useMemo(() => {
    if (cachedInvite === null && data !== undefined) {
      cachedInvite = data;
    }
    return cachedInvite;
  }, [data]);
  const { env } = useServerProps();
  const { Track, trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'sign-up',
    details: {
      deviceId,
    },
  });

  // Track error but still allows user to sign up
  useEffect(() => {
    if (error) {
      trackEvent({
        verb: 'errored',
        object: 'invite-query',
        details: {
          errorMessage: error.message,
        },
      });
    }
  }, [error, trackEvent]);

  useEffect(() => {
    const id = inviteData?.inviteByToken?.invite?.uuid;
    if (id) {
      window?.FS?.(id);
      window?.FS?.identify(id, {
        email: inviteData?.inviteByToken?.invite?.email ?? '',
      });
    }
  }, [inviteData, env, router]);

  if (
    !inviteData &&
    !error &&
    inviteToken !== undefined &&
    inviteToken !== ''
  ) {
    return <SpinnerFullPage />;
  }

  // Default flow return
  return (
    <Track>
      <OnboardingInner
        invite={inviteData?.inviteByToken?.invite || undefined}
      />
    </Track>
  );
}
